import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Landing from "@/pages/Landing";
import Auth from "@/pages/Auth";
import Legal from "@/pages/Legal";
import { ForgotPassword, ResetPassword } from "@/pages/PasswordReset";
import Subscribe from "@/pages/Subscribe";
import AppShell from "@/components/AppShell";
import Dashboard from "@/pages/Dashboard";
import Forecast from "@/pages/Forecast";
import Purchasing from "@/pages/Purchasing";
import WeatherIntel from "@/pages/WeatherIntel";
import Insights from "@/pages/Insights";
import Inventory from "@/pages/Inventory";
import Accuracy from "@/pages/Accuracy";
import Events from "@/pages/Events";
import Waste from "@/pages/Waste";
import Settings from "@/pages/Settings";
import OrgDashboard from "@/pages/OrgDashboard";
import { PaymentSuccess, PaymentCancel } from "@/pages/PaymentStatus";

function Protected({ children }) {
  const { user, subscription } = useAuth();
  if (user === null)
    return <div className="min-h-screen grid place-items-center text-zinc-400">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  // Unpaid accounts must pick a subscription before using the app, unless
  // they're on an active card-free pilot trial (plan stays "none" but
  // /subscription reports trial days). /subscribe itself is exempt —
  // otherwise it would loop forever.
  const onTrial = subscription?.trial_days_left != null;
  if (
    user.plan === "none" &&
    !onTrial &&
    window.location.pathname !== "/subscribe"
  )
    return <Navigate to="/subscribe" replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Auth />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/terms" element={<Legal type="terms" />} />
          <Route path="/privacy" element={<Legal type="privacy" />} />
          <Route path="/refunds" element={<Legal type="refunds" />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route
            path="/subscribe"
            element={
              <Protected>
                <Subscribe />
              </Protected>
            }
          />
          <Route
            path="/app"
            element={
              <Protected>
                <AppShell />
              </Protected>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="forecast" element={<Forecast />} />
            <Route path="purchasing" element={<Purchasing />} />
            <Route path="weather" element={<WeatherIntel />} />
            <Route path="insights" element={<Insights />} />
            <Route path="inventory" element={<Inventory />} />
            <Route path="accuracy" element={<Accuracy />} />
            <Route path="events" element={<Events />} />
            <Route path="waste" element={<Waste />} />
            <Route path="settings" element={<Settings />} />
            <Route path="org" element={<OrgDashboard />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
